<template lang="pug">
div(v-if="wordDoc && !audioDoc && (props.screen?.count || quiz?.count)")
  uc-text.italic(
    :values="{ count, includedQuestionCount: questionsToShown?.length }"
    text="[ **Note:** The **${count}** questions presented to the user are selected randomly from the following set of **${includedQuestionCount}** questions. ]"
    type="p"
  )

.screen-quiz
  uc-scroll-section(
    :data-bank-id="screen.id"
    :id="`${screen.id}-section${i}`"
    v-for="(q, i) in questionsToShown"
  )
    .wrapper
      audio-marker(:name="q.id") 
      uc-screen(:editPath="`quizzes.${i}.questions.${q.id}`" :screen="q")
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onMounted,
  provide,
  ref,
  unref,
  watch,
  withDefaults,
  type Ref
} from 'vue';
import shuffle from '../lib/shuffle';
import useFilterTags from '../lib/useFilterTags';
import useQuizzes from '../stores/useQuizzes';
import useSuspend from '../lib/useSuspend';
import { set } from '../lib/SCORM';
import { clearQueue, play, stop } from '../lib/audio';
import {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';

const { getQuiz } = useQuizzes();
const filterTags = useFilterTags();
const props = withDefaults(
  defineProps<{
    screen: ScreenQuiz;
    editPath: string;
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);
const quiz = computed<Quiz | undefined>(() => getQuiz(props.screen.from));
const suspend = useSuspend();
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));

provide('sectionId', props.screen.id);

const questions = computed(
  () =>
    quiz.value?.questions?.map(q => ({
      ...q,
      fullscreen: true,
      'for-quiz': props.screen.from
    })) as (ScreenQuestion | ScreenGridQuestion)[]
);

const count = computed(
  () => props.screen.count || quiz.value?.count || questions.value?.length
);

const shuffled = computed(() =>
  props.screen['shuffle-questions'] === false ? false : true
);

const questionsToShown = computed(() => {
  // see if we have saved to suspend the order of questions for this quiz
  const storedQuestions = suspend.quizQuestions[props.screen.from]
    ? JSON.parse(suspend.quizQuestions[props.screen.from])
    : [];
  if (storedQuestions.length > 0 && !audioDoc && !wordDoc) {
    // if we have stored questions, return them in the order they were stored
    return storedQuestions.map(questionId => {
      return questions.value.find(q => q.id === questionId);
    });
  } else {
    const filtered = filterTags(questions.value);

    // throw an error if we don't have any tagged questions
    if (filtered.length === 0) {
      console.log(
        `WARNING! No tagged questions found for quiz: ${props.screen.from}. Have we skipped over a builder question?`
      );
      console.log(
        `Questions: ${JSON.stringify(questions.value.map(q => q.id + ' - ' + JSON.stringify(q.tag)))}`
      );
      console.log(`Tags: ${JSON.stringify(suspend.tags)}`);
      return [];
    }

    // return the filtered questions in order
    if (audioDoc || wordDoc) return filtered;

    const compulsoryFiltered = !shuffled.value
      ? filtered.filter(q => q.compulsory)
      : shuffle(filtered.filter(q => q.compulsory));
    const optionalFiltered = !shuffled.value
      ? filtered.filter(q => !q.compulsory)
      : shuffle(filtered.filter(q => !q.compulsory));
    const remainingSlots = count.value - (compulsoryFiltered?.length ?? 0);
    const selectedFiltered = [
      ...compulsoryFiltered,
      ...optionalFiltered.slice(0, remainingSlots)
    ];

    const filteredAndShuffled = !shuffled.value
      ? selectedFiltered.slice(0, count.value)
      : (shuffle(selectedFiltered) as UcScreen[]).slice(0, count.value);
    const orderedQuestionIds = filteredAndShuffled.map(question => question.id);

    set(
      `quizQuestions.${props.screen.from}`,
      JSON.stringify(orderedQuestionIds)
    );

    return filteredAndShuffled;
  }
});

// First-question audio on initial quiz section entry (JiT and S3).
//
// Inner uc-scroll-sections only become the page's currentSection once the user
// scrolls into each question. On first entry the OUTER quiz section becomes
// currentSection (often while the quiz intro is still visible), so the first
// question's inner sectionActive never fires and uc-scroll-section.playAudio
// is skipped for the outer section (hasQuiz guard).
//
// Inject the outer sectionActive and play the first question intro here.
// Later questions use each inner uc-screen's sectionActive watcher as usual.
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
const { resolveChildIntro } = useChildScreenAudio();

const playFirstQuestionIntro = async () => {
  const first = questionsToShown.value?.[0] as NarratableScreen | undefined;
  if (!first?.id) return;

  clearQueue();
  stop();

  const audio = await resolveChildIntro(first);
  if (audio) await play(audio, { owner: first.id });
};

watch(sectionActive, async active => {
  if (props.nested) return;
  if (!active) return;
  await playFirstQuestionIntro();
});

// Mirror uc-screen.vue: sectionActive can already be true at mount with no
// false→true transition (e.g. quiz is the first section on the page).
onMounted(async () => {
  await nextTick();
  if (props.nested) return;
  if (!sectionActive.value) return;
  await playFirstQuestionIntro();
});
</script>

<style>
.screen-quiz + .screen-next-button {
  display: none;
}
</style>
